import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import LandingPage from './components/LandingPage/LandingPage';
import AboutPage from './components/AboutPage/AboutPage';
import StudyInterface from './components/StudyInterface/StudyInterface';
import Roadmap from './components/Roadmap/Roadmap';
import { generateNotes, generateQuizCards, generateRoadmap } from './api/api';
import './App.css';

function App() {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleStart = async (topic, navigate) => {
    if (!topic) return;
    
    setLoading(true);
    try {
      const roadmapData = await generateRoadmap(topic);
      navigate('/roadmap', { 
        state: { 
          roadmap: roadmapData,
          mainTopic: topic
        } 
      });
    } catch (error) {
      console.error('Error generating roadmap:', error);
      setError('Failed to generate roadmap. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTopicSelect = async (selectedTopic, navigate) => {
    if (!selectedTopic) return;
    
    setLoading(true);
    try {
      // Show loading state
      navigate('/study', { 
        state: { 
          loading: true,
          topic: selectedTopic 
        } 
      });
      
      // Generate content
      const [notesData, cardsData] = await Promise.all([
        generateNotes(selectedTopic),
        generateQuizCards(selectedTopic)
      ]);
      
      // Navigate with the generated content
      navigate('/study', { 
        state: { 
          notes: notesData, 
          cards: cardsData, 
          topic: selectedTopic,
          loading: false
        } 
      });
    } catch (error) {
      console.error('Error generating content:', error);
      setError('Failed to generate study materials. Please try again.');
      navigate('/roadmap');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Router>
      <div className="app-container">
        {error && (
          <div className="error-banner">
            {error}
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}
        <Routes>
          <Route path="/" element={<LandingPageWrapper onGetStarted={handleStart} loading={loading} />} />
          <Route path="/roadmap" element={<RoadmapWrapper onTopicSelect={handleTopicSelect} loading={loading} />} />
          <Route path="/study" element={<StudyInterfaceWrapper />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </Router>
  );
}

function LandingPageWrapper({ onGetStarted, loading }) {
  const navigate = useNavigate();
  return <LandingPage onGetStarted={(topic) => onGetStarted(topic, navigate)} loading={loading} />;
}

function RoadmapWrapper({ onTopicSelect, loading }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { roadmap, mainTopic } = location.state || {};

  if (!roadmap) {
    return (
      <div className="error-container">
        <h2>No roadmap found</h2>
        <button onClick={() => navigate('/')} className="back-button">
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div>
      <nav className="study-nav">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back to Home
        </button>
        <h2>{mainTopic} - Learning Roadmap</h2>
      </nav>
      <Roadmap 
        roadmap={roadmap} 
        onTopicSelect={(topic) => onTopicSelect(topic, navigate)} 
      />
    </div>
  );
}

function StudyInterfaceWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const { notes, cards, topic, loading } = location.state || {};

  if (loading) {
    return (
      <div>
        <nav className="study-nav">
          <button onClick={() => navigate(-1)} className="back-button">
            ← Back to Roadmap
          </button>
          <h2>{topic}</h2>
        </nav>
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Generating study materials for "{topic}"...</p>
        </div>
      </div>
    );
  }

  if (!notes || !cards) {
    return (
      <div className="error-container">
        <h2>No study materials found</h2>
        <button onClick={() => navigate('/')} className="back-button">
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div>
      <nav className="study-nav">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back to Roadmap
        </button>
        <h2>{topic}</h2>
      </nav>
      <StudyInterface notes={notes} cards={cards} />
    </div>
  );
}

function NotFound() {
  const navigate = useNavigate();
  return (
    <div className="not-found">
      <h2>Page Not Found</h2>
      <p>The page you're looking for doesn't exist.</p>
      <button onClick={() => navigate('/')} className="back-button">
        Return to Home
      </button>
    </div>
  );
}

export default App;